import { TCommonData, TWidget, TViewCommonOption } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, CSSProperties, ElementType, MouseEventHandler } from 'react';
import { useThemeHook } from '../hooks/themeHook';
import { TCoreButtonData } from '../types/data';
import { TCoreButtonOptions, ALIGNMENT_ENUM, TCoreOptions } from '../types/options';
import { buildButtonCssVars } from './buildButtonCssVars';
import { mergeWithTheme } from '../utils/styles';
import styles from './button.module.css';

export type TCoreButtonStyleProps = Pick<
    TCoreButtonOptions,
    | 'disabled'
    | 'hovered'
    | 'pressed'
    | 'fillContainer'
    | 'alignment'
    | 'borderRadius'
    | 'borderStroke'
    | 'elevation'
    | 'width'
    | 'maxWidth'
    | 'height'
    | 'maxHeight'
    | 'backgroundType'
    | 'textAlign'
    | 'maxLines'
    | 'padding'
    | 'iconSrc'
    | 'iconSize'
    | 'labelPlacement'
    | 'variant'
    | 'main'
    | 'onMain'
    | 'outline'
    | 'surface'
    | 'font'
    | 'fontStyle'
    | 'boxShadow'
    | 'margin'
    | 'cursorDisabled'
    | 'gap'
    | 'widthTransition'
>;

const ALIGNMENT_CLASS: Record<string, string> = {
    [ALIGNMENT_ENUM.TOP_LEFT]: styles.alignTopLeft,
    [ALIGNMENT_ENUM.TOP_CENTER]: styles.alignTopCenter,
    [ALIGNMENT_ENUM.TOP_RIGHT]: styles.alignTopRight,
    [ALIGNMENT_ENUM.CENTER_LEFT]: styles.alignCenterLeft,
    [ALIGNMENT_ENUM.CENTER]: styles.alignCenter,
    [ALIGNMENT_ENUM.CENTER_RIGHT]: styles.alignCenterRight,
    [ALIGNMENT_ENUM.BOTTOM_LEFT]: styles.alignBottomLeft,
    [ALIGNMENT_ENUM.BOTTOM_CENTER]: styles.alignBottomCenter,
    [ALIGNMENT_ENUM.BOTTOM_RIGHT]: styles.alignBottomRight,
};

export type TCoreButtonViewProps = {
    uid?: TWidget<TCommonData>['uid'];
    /** Used solely for theme resolution via useThemeHook. No behavior or interaction logic is derived from it here. */
    widget: TWidget<TCoreButtonData, TCoreButtonOptions>;
    text?: string;
    onClick: MouseEventHandler<HTMLButtonElement>;
    children?: React.ReactNode;
    as?: ElementType;
    /** Style overrides merged on top of the resolved theme palette via mergeWithTheme. */
    styleProps?: TCoreButtonStyleProps;
} & Pick<TViewCommonOption, 'isDisabled' | 'isHide' | 'styleName' | 'style' | 'parentStyleNames'>;

/**
 * Composable building block for use inside complex widgets.
 * Owns only the theme hook (useThemeHook) to keep the hook count low
 * when embedded inside larger widget trees.
 *
 * Accepts pre-resolved behavior props (isDisabled, isHide, onClick) from
 * the parent, making it suitable for scenarios where behavior state is
 * already managed one level up.
 */
const CoreButtonView = ({
    uid,
    widget,
    isDisabled,
    isHide,
    onClick,
    children,
    text,
    as: Tag = 'button',
    styleProps,
    styleName,
    parentStyleNames,
}: TCoreButtonViewProps) => {
    const enrichedWidget = { ...widget, options: { ...widget?.options, style: styleName, parentStyleNames } } as TWidget<TCommonData, TCoreOptions>;
    const { palette: themePalette } = useThemeHook(enrichedWidget);
    const palette: TCoreButtonOptions = mergeWithTheme(themePalette, styleProps);

    return (
        <Activity mode={isHide ? 'hidden' : 'visible'}>
            <Tag
                data-test={uid ?? widget?.uid}
                className={clsx(
                    styles.btn,
                    !palette.width && (palette.fillContainer ? styles.fullWidth : styles.fitContent),
                    palette.alignment && ALIGNMENT_CLASS[palette.alignment],
                )}
                aria-disabled={isDisabled}
                aria-label={text}
                aria-hidden={isHide}
                disabled={isDisabled}
                style={buildButtonCssVars(palette) as CSSProperties}
                onClick={onClick}
            >
                {children ?? text}
            </Tag>
        </Activity>
    );
};

export { CoreButtonView };
